import React, { useContext, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { Button, message, Tooltip, Upload, UploadFile, UploadProps } from 'antd'
import styled from 'styled-components'
import { FilePlus, FileMinus } from 'react-feather'

import { FlexRow } from '../../FlexboxReusables/FlexItems'
import { EditorSettingsContext } from '../EditorSettingsContext'
import { CourseContext } from '../../Course/CourseContext'
import { Course } from '../../Course/types'
import Modal from '../../Modal/Modal'
import OptionsMenu from './OptionsMenu'
import TestCourseAccessManagement from '../../Modal/TestCourseAccessManagement'
import { FileSyncOutlined, UploadOutlined } from '@ant-design/icons'
import { FileContext } from '../../Course/FileContext'

type EditorSettingsRowProps = {
  course: Course
  selectedPage: number
  setSelectedPage: (pageNumber: number) => void
  deleteCourse: (id: number) => void
}

const EditorSettingsRow: React.FC<EditorSettingsRowProps> = ({ ...props }) => {
  const { course, selectedPage, setSelectedPage, deleteCourse } = props
  const { editorSettingsContext } = useContext(EditorSettingsContext)
  const { setSelectedFile } = useContext(FileContext)
  const { updateCourse, saveCourse, deleteCoursePage } = useContext(CourseContext)

  const [showReplacePdfModal, setShowReplacePdfModal] = useState(false)
  const [fileList, setFileList] = useState<Array<UploadFile>>([])
  const [replacing, setReplacing] = useState(false)
  const [cacheBust, setCacheBust] = useState<number>(Date.now())

  const [showDeletePageModal, setShowDeletePageModal] = useState(false)
  const [showTestCourseAccessModal, setShowTestCourseAccessModal] = useState(false)
  const [existingTestCourseAccesses, setExistingTestCourseAccesses] = useState<ExistingTestCourseAccesses>([])

  const intl = useIntl()
  const navigate = useNavigate()

  const uploadProps: UploadProps = {
    accept: 'application/pdf',
    onRemove: (file) => {
      const index = fileList.indexOf(file)
      const newFileList = fileList.slice()
      newFileList.splice(index, 1)
      setFileList(newFileList)
    },
    beforeUpload: (file) => {
      setFileList([...fileList, file])

      return false
    },
    onChange: (info) => {
      if (info.file.status === 'done') {
        const file = info.file.originFileObj as File

        setSelectedFile(file)
      }
    },
    fileList
  }

  const goToFromPdfReplace = () => {
    if (!fileList.length) return
    navigate('/course/fromPdf', {
      state: {
        fromPdf: true,
        pdfFile: fileList[0],
        replacePdfForCourseId: course.id
      }
    })
    setShowReplacePdfModal(false)
    setFileList([])
  }

  const addNewPage = () => {
    if (!course) {
      console.error('Course.tsx: Course object is null.')

      return
    }

    if (editorSettingsContext.addNewPagesAtTheEnd) {
      const newCourse = {
        ...course,
        pages: [
          ...course.pages,
          {
            pageNumber: course.pages.length + 1,
            previewPicture: '',
            previewPictureBase64: '',
            previewPictureIsFromPdf: false,
            content: {
              ops: [
                {
                  insert: '',
                },
              ],
            }
          },
        ]
      }

      updateCourse(newCourse)
    }

    if (editorSettingsContext.addNewPagesAfterCurrentPage) {
      const currentPageIndex = course.pages.findIndex((page) => page.pageNumber === selectedPage + 1)
      const pagesBeforeAndIncludingCurrent = course.pages.filter((page) => page.pageNumber < selectedPage + 2)

      const newPage = {
        pageNumber: selectedPage + 2,
        previewPicture: '',
        previewPictureBase64: '',
        previewPictureIsFromPdf: false,
        content: {
          ops: [
            {
              insert: '',
            },
          ],
        }
      }

      const pagesAfterCurrent = course.pages.slice(currentPageIndex + 1).map((page, index) => ({
        ...page,
        pageNumber: selectedPage + 3 + index
      }))

      updateCourse({
        ...course,
        pages: [...pagesBeforeAndIncludingCurrent, newPage, ...pagesAfterCurrent]
      })
    }
  }

  const deletePage = () => {
    const pageIsLast = course.pages.length === selectedPage + 1
    if (pageIsLast) {
      setSelectedPage(selectedPage - 1)
    }

    deleteCoursePage(course.id, selectedPage, intl)
    setShowDeletePageModal(false)
  }

  return (
    <>
      {showDeletePageModal && (
        <Modal
          title={intl.formatMessage({ id: 'course.deleteCoursePage' }) + '?'}
          open={showDeletePageModal}
          onOk={() => deletePage()}
          onCancel={() => setShowDeletePageModal(false)}
          message={<FormattedMessage id="editor.confirmDeletePage" />}
        />
      )}
      {showTestCourseAccessModal && (
        <TestCourseAccessManagement
          course={course}
          showTestCourseAccessModal={showTestCourseAccessModal}
          setShowTestCourseAccessModal={setShowTestCourseAccessModal}
          existingTestCourseAccesses={existingTestCourseAccesses}
          setExistingTestCourseAccesses={setExistingTestCourseAccesses}
        />
      )}

      <FlexRow justifyContent='flex-end'>
        <>
          <OptionsMenu
            course={course}
            deleteCourse={deleteCourse}
          />
          {course.fromPdf && (
            <Tooltip title="PDF austauschen">
              <StyledButton onClick={() => setShowReplacePdfModal(true)}>
                <FileSyncOutlined />
              </StyledButton>
            </Tooltip>
          )}
          <Tooltip title={<FormattedMessage id='editor.deletePage' />}>
            <StyledButton onClick={() => setShowDeletePageModal(true)}>
              <FileMinus size={20} />
            </StyledButton>
          </Tooltip>
          <Tooltip title={<FormattedMessage id='editor.addNewPage' />}>
            <StyledButton onClick={() => addNewPage()}>
              <FilePlus size={20} />
            </StyledButton>
          </Tooltip>
          {course.isPublished && (
            <Button
              type='primary'
              onClick={() => setShowTestCourseAccessModal(true)}
              style={{ marginRight: '1em' }}
            >
              Zugänge
            </Button>
          )}
          <Button
            type='primary'
            onClick={() => saveCourse(course, intl)}
            disabled={!course.hasUnsavedChanges}
          >
            <FormattedMessage id='generic.save' />
          </Button>
        </>
      </FlexRow>

      {showReplacePdfModal && (
        <Modal
          title="PDF austauschen"
          open={showReplacePdfModal}
          onOk={goToFromPdfReplace}
          onCancel={() => { if (!replacing) setShowReplacePdfModal(false) }}
          message={
            <>
              <p>Neue PDF auswählen. Seitenzahl kann sich ändern – Inhalte/Fragen bleiben erhalten.</p>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />} disabled={replacing}>
                  {replacing ? 'Ersetze...' : 'PDF wählen'}
                </Button>
              </Upload>
              {!fileList.length && !replacing && <p style={{ color: '#999' }}>Bitte eine PDF auswählen, bevor fortgefahren wird.</p>}
            </>
          }
        />
      )}
    </>
  )
}

export default EditorSettingsRow

export type ExistingTestCourseAccesses = Array<{
  linkForParticipants: string
  examTakenCount: number
  paidParticipantsCount: number
  etPartnerName: string
}>

const StyledButton = styled(Button)`
  margin-right: 1em;
`